import React, { FC, useEffect, useState } from 'react';

import {
  Box,
  Button,
  MUITable,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from 'components';

import { _, useTranslation } from 'third-party';

import {
  DEFAULT_TOP_UP_QUANTITY,
  MAX_TOP_UP_QUANTITY_LENGTH,
  SUPPORT_EMAIL,
  USER_INPUT_DEBOUNCE_TIME_MS,
} from 'constants/common';

import { CheckPackQuote, CheckPackTier } from 'modules/billing';

import { formatUsd } from 'utils/helper';

type Props = {
  tiers: CheckPackTier[];
  quote: CheckPackQuote | null;
  isQuotePending: boolean;
  isCheckoutPending: boolean;
  isStripeConfigured: boolean;
  onQuantityChange: (quantity: number) => void;
  onCheckout: (quantity: number) => void;
};

export const TopUpPanel: FC<Props> = ({
  tiers,
  quote,
  isQuotePending,
  isCheckoutPending,
  isStripeConfigured,
  onQuantityChange,
  onCheckout,
}) => {
  const { t } = useTranslation();
  const [quantityInput, setQuantityInput] = useState<string>(
    String(DEFAULT_TOP_UP_QUANTITY),
  );

  const quantity = parseInt(quantityInput, 10);
  const hasQuantity = !isNaN(quantity) && quantity > 0;

  // Letters, signs and separators never reach the state, so only a whole number can be quoted.
  const handleQuantityChange = (value: string) =>
    setQuantityInput(
      value
        .replace(/[^0-9]/g, '')
        .replace(/^0+/, '')
        .slice(0, MAX_TOP_UP_QUANTITY_LENGTH),
    );

  // The quote is the server's, so the total shown is the total charged.
  useEffect(() => {
    if (!hasQuantity) return;

    const request = _.debounce(
      () => onQuantityChange(quantity),
      USER_INPUT_DEBOUNCE_TIME_MS,
    );
    request();

    return () => request.cancel();
  }, [quantity, hasQuantity, onQuantityChange]);

  const isQuoteForCurrentQuantity = quote?.quantity === quantity;
  const canCheckout =
    hasQuantity &&
    isStripeConfigured &&
    isQuoteForCurrentQuantity &&
    quote.isPurchasable &&
    !isQuotePending &&
    !isCheckoutPending;

  const rangeLabel = (tier: CheckPackTier) =>
    tier.maxQuantity === null
      ? t('billing.topUp.rangeOpen', { min: tier.minQuantity })
      : t('billing.topUp.range', {
          min: tier.minQuantity,
          max: tier.maxQuantity,
        });

  return (
    <Box
      sx={{
        p: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }} mb={0.5}>
        {t('billing.topUp.header')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('billing.topUp.hint')}
      </Typography>

      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="flex-start"
        spacing={2}
        useFlexGap
      >
        <TextField
          size="small"
          label={t('billing.topUp.quantity')}
          value={quantityInput}
          onChange={event => handleQuantityChange(event.target.value)}
          error={!hasQuantity}
          helperText={!hasQuantity && t('billing.topUp.quantityInvalid')}
          slotProps={{
            htmlInput: {
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: MAX_TOP_UP_QUANTITY_LENGTH,
            },
          }}
          sx={{ width: '10rem' }}
        />

        <Box minWidth="12rem">
          <Typography variant="body2" color="text.secondary">
            {t('billing.topUp.total')}
          </Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
            {isQuoteForCurrentQuantity && quote.isPurchasable
              ? t('billing.topUp.totalValue', {
                  total: formatUsd(quote.totalAmount),
                })
              : '—'}
          </Typography>
          {isQuoteForCurrentQuantity && quote.isPurchasable && (
            <Typography variant="body2" color="text.secondary">
              {t('billing.topUp.perCheck', {
                price: formatUsd(quote.pricePerCheck),
              })}
            </Typography>
          )}
        </Box>

        {isQuoteForCurrentQuantity && quote.requiresSales ? (
          <Button
            variant="outlined"
            size="small"
            sx={{ alignSelf: 'center' }}
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
              t('billing.topUp.salesSubject', { count: quantity }),
            )}`}
          >
            {t('billing.topUp.contactSales')}
          </Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            disabled={!canCheckout}
            onClick={() => onCheckout(quantity)}
            sx={{
              alignSelf: 'center',
              color: 'primary.contrastText',
              '&.Mui-disabled': { color: 'primary.contrastText' },
            }}
          >
            {t('billing.topUp.buy')}
          </Button>
        )}
      </Stack>

      {isQuoteForCurrentQuantity && !quote.isPurchasable && (
        <Typography variant="body2" color="error.main" mt={1}>
          {quote.unavailableReason}
        </Typography>
      )}

      {!isStripeConfigured && (
        <Typography variant="body2" color="text.secondary" mt={1}>
          {t('billing.stripeUnavailable')}
        </Typography>
      )}

      <Box mt={2.5} sx={{ overflowX: 'auto' }}>
        <Typography variant="body2" color="text.secondary" mb={0.5}>
          {t('billing.topUp.tableHeader')}
        </Typography>
        <MUITable size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>
                {t('billing.topUp.column.quantity')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                {t('billing.topUp.column.pricePerCheck')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tiers.map(tier => (
              <TableRow
                key={tier.id}
                sx={{
                  backgroundColor:
                    isQuoteForCurrentQuantity &&
                    quantity >= tier.minQuantity &&
                    quantity <= (tier.maxQuantity ?? Number.MAX_SAFE_INTEGER)
                      ? 'action.hover'
                      : 'transparent',
                }}
              >
                <TableCell>{rangeLabel(tier)}</TableCell>
                <TableCell>
                  {t('billing.topUp.perCheckValue', {
                    price: formatUsd(tier.pricePerCheck),
                  })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </MUITable>
        <Typography variant="body2" color="text.secondary" mt={1}>
          {t('billing.topUp.salesNote', {
            max: quote?.maxQuantity ?? 0,
          })}
        </Typography>
      </Box>
    </Box>
  );
};
